/**
 * SignalTracker.js
 * 
 * A class to track and manage signal detection state with proper lifecycle management.
 * Replaces global state variables in signalDetection.js with a more maintainable approach.
 */

/**
 * SignalTracker class for maintaining state of detected signals
 */
export class SignalTracker {
  constructor(options = {}) {
    this.signals = {}; // Format: {indicatorType: {signalKey: {lastValue, lastTimestamp}}}
    this.options = {
      persistToLocalStorage: false,
      storageKey: 'tradefolio_signal_tracker',
      logLevel: 'normal',
      ...options
    };
    
    // Initialize from localStorage if enabled
    if (this.options.persistToLocalStorage) {
      this.loadFromStorage();
    }
    
    this.log('SignalTracker initialized', { level: 'debug' });
  }

  /**
   * Generate a unique key for a signal
   * 
   * @param {string} indicatorType - Type of indicator
   * @param {number|string} threshold - Threshold value
   * @param {string} operator - Comparison operator
   * @returns {string} - Unique signal key
   */
  getSignalKey(indicatorType, threshold, operator) {
    return `${indicatorType}_${threshold}_${operator}`;
  }

  /**
   * Track a value for a specific indicator/threshold/operator combination
   * 
   * @param {string} indicatorType - Type of indicator
   * @param {number|string} threshold - Threshold value
   * @param {string} operator - Comparison operator
   * @param {number} value - Current value
   * @param {number} timestamp - Current timestamp
   */
  trackValue(indicatorType, threshold, operator, value, timestamp = Date.now()) {
    const signalKey = this.getSignalKey(indicatorType, threshold, operator);
    
    // Initialize indicatorType if not exists
    if (!this.signals[indicatorType]) {
      this.signals[indicatorType] = {};
    }
    
    // Update signal data
    this.signals[indicatorType][signalKey] = {
      lastValue: value,
      lastTimestamp: timestamp
    };
    
    this.log(`Tracked value ${value} for ${signalKey}`, { level: 'verbose' });
    
    // Persist to storage if enabled
    if (this.options.persistToLocalStorage) {
      this.saveToStorage();
    }
  }

  /**
   * Get the last tracked value for a signal
   * 
   * @param {string} indicatorType - Type of indicator
   * @param {number|string} threshold - Threshold value
   * @param {string} operator - Comparison operator
   * @returns {number|null} - Last tracked value or null if not found
   */
  getLastValue(indicatorType, threshold, operator) {
    const signalKey = this.getSignalKey(indicatorType, threshold, operator);
    
    if (!this.signals[indicatorType] || !this.signals[indicatorType][signalKey]) {
      this.log(`No previous value found for ${signalKey}`, { level: 'debug' });
      return null;
    }
    
    return this.signals[indicatorType][signalKey].lastValue;
  }

  /**
   * Get the last timestamp for a signal
   * 
   * @param {string} indicatorType - Type of indicator
   * @param {number|string} threshold - Threshold value
   * @param {string} operator - Comparison operator
   * @returns {number|null} - Last timestamp or null if not found
   */
  getLastTimestamp(indicatorType, threshold, operator) {
    const signalKey = this.getSignalKey(indicatorType, threshold, operator);
    
    if (!this.signals[indicatorType] || !this.signals[indicatorType][signalKey]) {
      return null;
    }
    
    return this.signals[indicatorType][signalKey].lastTimestamp;
  }

  /**
   * Check if a signal has been detected recently
   * 
   * @param {string} indicatorType - Type of indicator
   * @param {number|string} threshold - Threshold value
   * @param {string} operator - Comparison operator
   * @param {number} withinMs - Time window to consider "recent" in milliseconds
   * @returns {boolean} - Whether signal was detected within the specified time window
   */
  wasDetectedRecently(indicatorType, threshold, operator, withinMs = 3600000) {
    const lastTimestamp = this.getLastTimestamp(indicatorType, threshold, operator);
    
    if (!lastTimestamp) {
      return false;
    }
    
    const timeSince = Date.now() - lastTimestamp;
    return timeSince < withinMs;
  }

  /**
   * Load signal state from localStorage
   */
  loadFromStorage() {
    try {
      const stored = localStorage.getItem(this.options.storageKey);
      if (stored) {
        this.signals = JSON.parse(stored);
        this.log(`Loaded ${Object.keys(this.signals).length} indicator types from storage`, { level: 'info' });
      }
    } catch (error) {
      console.error('[SignalTracker] Error loading from localStorage:', error);
    }
  }

  /**
   * Save signal state to localStorage
   */
  saveToStorage() {
    try {
      localStorage.setItem(this.options.storageKey, JSON.stringify(this.signals));
      this.log('Saved signal state to localStorage', { level: 'debug' });
    } catch (error) {
      console.error('[SignalTracker] Error saving to localStorage:', error);
    }
  }

  /**
   * Clear all signal tracking data
   */
  clear() {
    this.signals = {};
    
    if (this.options.persistToLocalStorage) {
      try {
        localStorage.removeItem(this.options.storageKey);
      } catch (error) {
        console.error('[SignalTracker] Error clearing localStorage:', error);
      }
    }
    
    this.log('Signal tracking data cleared', { level: 'info' });
  }

  /**
   * Clear tracking data for a specific indicator
   * 
   * @param {string} indicatorType - Type of indicator to clear
   */
  clearIndicator(indicatorType) {
    if (this.signals[indicatorType]) {
      delete this.signals[indicatorType];
      
      if (this.options.persistToLocalStorage) {
        this.saveToStorage();
      }
      
      this.log(`Cleared signal data for indicator: ${indicatorType}`, { level: 'info' });
    }
  }

  /**
   * Get all tracked indicators
   * 
   * @returns {string[]} - List of tracked indicator types
   */
  getTrackedIndicators() {
    return Object.keys(this.signals);
  }

  /**
   * Get all tracked signals for an indicator
   * 
   * @param {string} indicatorType - Type of indicator
   * @returns {Object} - Map of signal keys to their data
   */
  getIndicatorSignals(indicatorType) {
    return this.signals[indicatorType] || {};
  }

  /**
   * Get a summary of all tracking data
   * 
   * @returns {Object} - Summary of tracking data
   */
  getSummary() {
    const summary = {
      indicatorCount: Object.keys(this.signals).length,
      indicators: {},
      totalSignals: 0
    };
    
    for (const indicatorType in this.signals) {
      const signalCount = Object.keys(this.signals[indicatorType]).length;
      summary.indicators[indicatorType] = signalCount;
      summary.totalSignals += signalCount;
    }
    
    return summary;
  }

  /**
   * Internal logging method
   * 
   * @param {string} message - Log message
   * @param {Object} options - Logging options
   */
  log(message, { level = 'normal', ...rest } = {}) {
    const logLevels = {
      silent: 0,
      error: 1,
      warn: 2,
      info: 3,
      normal: 4,
      debug: 5,
      verbose: 6
    };
    
    const configLevel = logLevels[this.options.logLevel] || 4;
    const messageLevel = logLevels[level] || 4;
    
    if (messageLevel <= configLevel) {
      if (level === 'error') {
        console.error(`[SignalTracker] ${message}`, rest);
      } else if (level === 'warn') {
        console.warn(`[SignalTracker] ${message}`, rest);
      } else if (level === 'debug' || level === 'verbose') {
        console.debug(`[SignalTracker] ${message}`, rest);
      } else {
        console.log(`[SignalTracker] ${message}`, rest);
      }
    }
  }
}

// Create a default instance for common use
export const defaultTracker = new SignalTracker();

export default SignalTracker;
